import { RefObject, useEffect, useState } from 'react';

import {
  Cue, getCueText, getFaqCaptionsUrl, parseVtt,
} from './faqVideo';

interface YouTubePlayer {
  getCurrentTime: () => number;
}

interface YouTubeApi {
  Player: new (iframe: HTMLIFrameElement, options: { events: { onReady: () => void } }) => YouTubePlayer;
}

declare global {
  interface Window {
    YT?: YouTubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';
const POLL_MS = 250;

const loadYouTubeApi = (): Promise<YouTubeApi> => new Promise((resolve) => {
  if (window.YT?.Player) {
    resolve(window.YT);
    return;
  }
  const previous = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    previous?.();
    resolve(window.YT as YouTubeApi);
  };
  if (!document.querySelector(`script[src="${YOUTUBE_API_SRC}"]`)) {
    const script = document.createElement('script');
    script.src = YOUTUBE_API_SRC;
    script.async = true;
    document.head.appendChild(script);
  }
});

/** Subtitles for the embedded FAQ video, timed against the player via the YouTube IFrame API. */
export const useFaqVideoCaptions = (iframeRef: RefObject<HTMLIFrameElement>, locale: string, enabled: boolean) => {
  const [cues, setCues] = useState<Cue[]>([]);
  const [player, setPlayer] = useState<YouTubePlayer | null>(null);
  const [text, setText] = useState('');

  useEffect(() => {
    let active = true;
    fetch(getFaqCaptionsUrl(locale))
      .then((response) => (response.ok ? response.text() : ''))
      .then((vtt) => { if (active) { setCues(parseVtt(vtt)); } })
      .catch(() => { if (active) { setCues([]); } });
    return () => { active = false; };
  }, [locale]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!enabled || !cues.length || player || !iframe) { return undefined; }
    let active = true;
    loadYouTubeApi().then((YT) => {
      const bound = new YT.Player(iframe, { events: { onReady: () => { if (active) { setPlayer(bound); } } } });
    });
    return () => { active = false; };
  }, [enabled, cues, player, iframeRef]);

  useEffect(() => {
    if (!enabled || !player) { return undefined; }
    const timer = window.setInterval(() => setText(getCueText(cues, player.getCurrentTime())), POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, player, cues]);

  return { hasCaptions: cues.length > 0, text: enabled ? text : '' };
};
